<script setup lang="ts">
/**
 * Сервисы задачи — макеты 272:2913 (карточка) и 273:2913 (форма создания).
 *
 * Список репозиториев, которые поедут в её фича-стенд. Задаётся при постановке:
 * без этого стенд поднимался бы одним бэкендом, а смотреть фичу было бы не в
 * чем (DEV-1626).
 *
 * Подсказка внизу — репозиторий, где ветка задачи уже есть, а в составе его
 * нет. Предлагается, но сама в состав не идёт: список правит человек, а не
 * случайно названная ветка.
 */
import { AppChip } from '@vinprop/design-system'
import { Plus } from 'lucide-vue-next'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import InlinePicker from '@/components/InlinePicker.vue'

import { shortService } from '../format'
import { listRepositories } from '../repositories'

const props = defineProps<{
  /** Идентификаторы проектов GitLab — то, что понимает ручка состава */
  modelValue: string[]
  /** Репозитории с веткой задачи, которых нет в составе */
  suggestions?: { projectId: string; path: string }[]
  disabled?: boolean
}>()

const emit = defineEmits<{ 'update:modelValue': [ids: string[]] }>()

const { t } = useI18n()

const repositories = ref<{ id: string; path: string }[]>([])

onMounted(async () => {
  try {
    const { repositories: list } = await listRepositories('connected')

    repositories.value = list
      .filter((repository) => !repository.archived)
      .map((repository) => ({ id: String(repository.id), path: repository.path }))
  } catch {
    // Без реестра остаются уже выбранные сервисы: они показываются путями из
    // подсказок и состава, а добавить новый будет нечем — до перезагрузки.
    repositories.value = []
  }
})

const pathOf = (id: string) =>
  repositories.value.find((repository) => repository.id === id)?.path ?? id

const picked = computed(() => props.modelValue.map((id) => ({ id, path: pathOf(id) })))

/** В выборе — всё, чего ещё нет в составе */
const options = computed(() =>
  repositories.value
    .filter((repository) => !props.modelValue.includes(repository.id))
    .map((repository) => ({ value: repository.id, label: repository.path })),
)

const fresh = computed(() =>
  (props.suggestions ?? []).filter((item) => !props.modelValue.includes(item.projectId)),
)

const add = (id: string) => {
  if (!id || props.modelValue.includes(id)) return

  emit('update:modelValue', [...props.modelValue, id])
}

const remove = (id: string) => {
  emit(
    'update:modelValue',
    props.modelValue.filter((item) => item !== id),
  )
}
</script>

<template>
  <div class="services">
    <div class="services__chips">
      <AppChip
        v-for="service in picked"
        :key="service.id"
        size="small"
        :label="shortService(service.path)"
        :title="service.path"
        :removable="!disabled"
        @remove="remove(service.id)"
      />

      <!-- Панель прижата правым краем: поле стоит в правой колонке карточки, и
           влево ей раскрываться некуда — уезжает за окно. -->
      <InlinePicker
        v-if="!disabled"
        :options="options"
        model-value=""
        :empty-text="t('devops.services.noRepositories')"
        align="right"
        searchable
        @update:model-value="add"
      >
        <template #trigger="{ toggle }">
          <button
            type="button"
            class="services__add"
            :aria-label="t('devops.services.add')"
            :title="t('devops.services.add')"
            @click="toggle()"
          >
            <Plus :size="13" />
          </button>
        </template>
      </InlinePicker>
    </div>

    <p v-for="item in fresh" :key="item.projectId" class="services__hint">
      {{ t('devops.services.suggested', { name: shortService(item.path) }) }}
      <button
        v-if="!disabled"
        type="button"
        class="services__link"
        @click="add(item.projectId)"
      >
        {{ t('devops.services.addSuggested') }}
      </button>
    </p>
  </div>
</template>

<style scoped>
.services {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.services__chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

/* Кнопка «+» — тот же размер, что и чип рядом: макет 272:2913, 20 на 20 */
.services__add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.services__add:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.services__hint {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  line-height: 15px;
}

.services__link {
  padding: 0;
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: inherit;
  font-size: var(--font-size-overline);
  cursor: pointer;
}
</style>
